import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import {
  archiveManualEvent,
  createManualEvent,
  deleteManualEvent,
  fetchInfoScreenEventsCatalog,
  fetchInfoScreenEventsConfig,
  fetchManualEvents,
  updateInfoScreenEventsConfig,
  updateManualEvent,
} from "@/lib/infoScreenEventsApi";
import { formatDateTimeShort } from "@/lib/bookingDates";
import { InfoScreenEventsModule } from "@/pages/info-screen/components/InfoScreenEventsModule";
import {
  EVENT_CATEGORIES,
  EVENT_TIME_RANGES,
  type InfoScreenEventCatalogItem,
  type InfoScreenEventsConfig,
  type InfoScreenManualEvent,
} from "@/types/infoScreenEvents";
import "@/pages/info-screen/info-screen.css";

type EventsSettings = Record<string, unknown>;

function emptyManualEvent(): Omit<
  InfoScreenManualEvent,
  "id" | "archived_at" | "created_at" | "updated_at"
> {
  const start = new Date();
  start.setHours(19, 0, 0, 0);
  return {
    title: "",
    starts_at: start.toISOString(),
    ends_at: null,
    time_label: "19:00",
    venue_name: "",
    city: "Frankfurt am Main",
    latitude: 50.1109,
    longitude: 8.6821,
    category: "culture",
    description: "",
    image_url: null,
    event_url: null,
    priority: 10,
    is_active: true,
  };
}

export function InfoScreenEventsPanel({ canEdit }: { canEdit: boolean }) {
  const [config, setConfig] = useState<InfoScreenEventsConfig | null>(null);
  const [manualEvents, setManualEvents] = useState<InfoScreenManualEvent[]>([]);
  const [catalogEvents, setCatalogEvents] = useState<InfoScreenEventCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [editing, setEditing] = useState<InfoScreenManualEvent | null>(null);
  const [draft, setDraft] = useState(emptyManualEvent());

  const eventsSettings = (config?.events ?? {}) as EventsSettings;
  const excludedEventIds = useMemo(
    () => new Set((eventsSettings.excluded_event_ids as string[] | undefined) ?? []),
    [eventsSettings.excluded_event_ids],
  );

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const catalog = await fetchInfoScreenEventsCatalog();
      setCatalogEvents(catalog);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Veranstaltungsliste konnte nicht geladen werden");
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cfg, manual] = await Promise.all([
        fetchInfoScreenEventsConfig(),
        fetchManualEvents(),
      ]);
      setConfig(cfg);
      setManualEvents(manual);
      void loadCatalog();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, [loadCatalog]);

  useEffect(() => {
    void load();
  }, [load]);

  function patchEvents(patch: EventsSettings) {
    if (!config) return;
    setConfig({
      ...config,
      events: { ...eventsSettings, ...patch },
    });
    setSuccess(false);
  }

  function setEventVisible(eventId: string, visible: boolean) {
    const next = new Set(excludedEventIds);
    if (visible) {
      next.delete(eventId);
    } else {
      next.add(eventId);
    }
    patchEvents({ excluded_event_ids: Array.from(next) });
    setCatalogEvents((items) =>
      items.map((item) =>
        item.id === eventId ? { ...item, is_visible: visible } : item,
      ),
    );
  }

  function formatCatalogDate(iso: string): string {
    return formatDateTimeShort(iso);
  }

  const sourceLabels: Record<string, string> = {
    manual: "Eigene",
    eventbrite: "Eventbrite",
    ticketmaster: "Ticketmaster",
    eventim: "Eventim",
  };

  async function onSaveConfig() {
    if (!config || !canEdit) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await updateInfoScreenEventsConfig({
        events: config.events,
        modules: config.modules,
      });
      setConfig(saved);
      setSuccess(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  async function onSaveManual() {
    if (!canEdit) return;
    setSaving(true);
    try {
      if (editing) {
        await updateManualEvent(editing.id, draft);
      } else {
        await createManualEvent(draft);
      }
      await load();
      setEditing(null);
      setDraft(emptyManualEvent());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Veranstaltungen werden geladen…</p>;
  }

  if (!config) {
    return <p className="text-sm text-red-600">{error ?? "Konfiguration nicht verfügbar"}</p>;
  }

  const previewEvents = catalogEvents
    .filter((e) => !excludedEventIds.has(e.id))
    .slice(0, Number(eventsSettings.max_visible ?? 6))
    .map((e) => ({
      id: e.id,
      source: e.source,
      title: e.title,
      starts_at: e.starts_at,
      ends_at: e.ends_at,
      time_label: e.time_label,
      venue_name: e.venue_name,
      city: e.city,
      category: e.category,
      description: e.description,
      image_url: e.image_url,
      event_url: e.event_url,
      priority: e.priority,
    }));

  return (
    <div className="space-y-6">
      <Card title="Vorschau Veranstaltungen" bodyClassName="p-0 overflow-hidden rounded-xl">
        <div className="guest-tablet-root info-screen-idle-preview" data-kiosk="info-screen-preview">
          <div className="info-screen-shell">
            <InfoScreenEventsModule
              cityLabel={String(eventsSettings.city_label ?? "Frankfurt am Main")}
              events={previewEvents}
              rotationSeconds={Number(eventsSettings.rotation_seconds ?? 10)}
            />
          </div>
        </div>
      </Card>

      <Card title="Veranstaltungen auswählen">
        <p className="mb-3 text-sm text-label-secondary">
          Alle geladenen Veranstaltungen aus den aktiven Quellen (nach Zeitraum und Radius).
          Deaktivieren Sie Einträge, die nicht auf dem Infobildschirm erscheinen sollen.
        </p>
        {catalogLoading ? (
          <p className="text-sm text-label-secondary">Veranstaltungen werden geladen…</p>
        ) : catalogEvents.length === 0 ? (
          <p className="text-sm text-label-secondary">
            Keine Veranstaltungen gefunden. Aktivieren Sie Datenquellen und synchronisieren Sie
            externe APIs, oder legen Sie eigene Veranstaltungen an.
          </p>
        ) : (
          <ul className="divide-y divide-separator text-sm max-h-[28rem] overflow-y-auto">
            {catalogEvents.map((ev) => {
              const visible = !excludedEventIds.has(ev.id);
              return (
                <li
                  key={ev.id}
                  className={`flex flex-wrap items-start gap-3 py-3 ${visible ? "" : "opacity-50"}`}
                >
                  <label className="flex shrink-0 items-center gap-2 pt-0.5">
                    <input
                      type="checkbox"
                      disabled={!canEdit}
                      checked={visible}
                      onChange={(e) => setEventVisible(ev.id, e.target.checked)}
                    />
                    <span className="sr-only">Anzeigen: {ev.title}</span>
                  </label>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{ev.title}</p>
                    <p className="text-label-secondary">
                      {formatCatalogDate(ev.starts_at)}
                      {ev.venue_name ? ` · ${ev.venue_name}` : ""}
                      {ev.city ? `, ${ev.city}` : ""}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full bg-fill-tertiary px-2 py-0.5 text-xs text-label-secondary">
                    {sourceLabels[ev.source] ?? ev.source}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        <p className="mt-3 text-xs text-label-secondary">
          {catalogEvents.filter((e) => !excludedEventIds.has(e.id)).length} von{" "}
          {catalogEvents.length} Veranstaltungen werden angezeigt (max.{" "}
          {Number(eventsSettings.max_visible ?? 6)} auf dem Bildschirm).
        </p>
        {canEdit ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="mt-3"
            onClick={() => void loadCatalog()}
            disabled={catalogLoading}
          >
            Liste aktualisieren
          </Button>
        ) : null}
      </Card>

      <Card title="Anzeige & Filter">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              disabled={!canEdit}
              checked={Boolean(eventsSettings.enabled)}
              onChange={(e) => patchEvents({ enabled: e.target.checked })}
            />
            Veranstaltungen auf Infobildschirmen anzeigen
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Max. sichtbare Events
            <input
              type="number"
              min={1}
              max={24}
              disabled={!canEdit}
              className="eh-input w-24"
              value={Number(eventsSettings.max_visible ?? 6)}
              onChange={(e) => patchEvents({ max_visible: Number(e.target.value) || 6 })}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Zeitraum
            <select
              disabled={!canEdit}
              className="eh-input"
              value={String(eventsSettings.time_range ?? "next_7_days")}
              onChange={(e) => patchEvents({ time_range: e.target.value })}
            >
              {EVENT_TIME_RANGES.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Radius (km um Frankfurt)
            <input
              type="number"
              min={5}
              max={200}
              disabled={!canEdit}
              className="eh-input w-28"
              value={Number(eventsSettings.radius_km ?? 50)}
              onChange={(e) => patchEvents({ radius_km: Number(e.target.value) || 50 })}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Karten-Rotation (Sek.)
            <input
              type="number"
              min={5}
              max={120}
              disabled={!canEdit}
              className="eh-input w-24"
              value={Number(eventsSettings.rotation_seconds ?? 10)}
              onChange={(e) => patchEvents({ rotation_seconds: Number(e.target.value) || 10 })}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm sm:col-span-2">
            Stadt-Bezeichnung
            <input
              type="text"
              disabled={!canEdit}
              className="eh-input"
              value={String(eventsSettings.city_label ?? "Frankfurt am Main")}
              onChange={(e) => patchEvents({ city_label: e.target.value })}
            />
          </label>
        </div>
      </Card>

      <Card title="Eigene Veranstaltungen pflegen">
        <div className="space-y-4">
          <ul className="divide-y divide-separator text-sm">
            {manualEvents.map((ev) => (
              <li key={ev.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span>
                  <strong>{ev.title}</strong>
                  {!ev.is_active || ev.archived_at ? " (inaktiv/archiviert)" : ""}
                </span>
                {canEdit ? (
                  <span className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setEditing(ev);
                        setDraft({
                          title: ev.title,
                          starts_at: ev.starts_at,
                          ends_at: ev.ends_at,
                          time_label: ev.time_label,
                          venue_name: ev.venue_name,
                          city: ev.city,
                          latitude: ev.latitude,
                          longitude: ev.longitude,
                          category: ev.category,
                          description: ev.description,
                          image_url: ev.image_url,
                          event_url: ev.event_url,
                          priority: ev.priority,
                          is_active: ev.is_active,
                        });
                      }}
                    >
                      Bearbeiten
                    </Button>
                    {!ev.archived_at ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => void archiveManualEvent(ev.id).then(() => load())}
                      >
                        Archivieren
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        if (window.confirm("Wirklich löschen?")) {
                          void deleteManualEvent(ev.id).then(() => load());
                        }
                      }}
                    >
                      Löschen
                    </Button>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>

          {canEdit ? (
            <div className="border-t border-separator pt-4 space-y-3">
              <h4 className="font-medium text-sm">
                {editing ? "Veranstaltung bearbeiten" : "Neue Veranstaltung"}
              </h4>
              <input
                className="eh-input w-full"
                placeholder="Titel"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-sm">
                  Beginn
                  <input
                    type="datetime-local"
                    className="eh-input w-full"
                    value={draft.starts_at.slice(0, 16)}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        starts_at: new Date(e.target.value).toISOString(),
                      })
                    }
                  />
                </label>
                <label className="text-sm">
                  Uhrzeit (Anzeige)
                  <input
                    className="eh-input w-full"
                    value={draft.time_label ?? ""}
                    onChange={(e) => setDraft({ ...draft, time_label: e.target.value })}
                  />
                </label>
              </div>
              <input
                className="eh-input w-full"
                placeholder="Ort / Venue"
                value={draft.venue_name ?? ""}
                onChange={(e) => setDraft({ ...draft, venue_name: e.target.value })}
              />
              <select
                className="eh-input w-full max-w-xs"
                value={draft.category ?? "other"}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              >
                {EVENT_CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
              <textarea
                className="eh-input w-full min-h-[80px]"
                placeholder="Kurzbeschreibung"
                value={draft.description ?? ""}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
              <input
                className="eh-input w-full"
                placeholder="Bild-URL"
                value={draft.image_url ?? ""}
                onChange={(e) => setDraft({ ...draft, image_url: e.target.value || null })}
              />
              <input
                className="eh-input w-full"
                placeholder="Link zur Veranstaltung"
                value={draft.event_url ?? ""}
                onChange={(e) => setDraft({ ...draft, event_url: e.target.value || null })}
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.is_active}
                  onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                />
                Aktiv
              </label>
              <div className="flex gap-2">
                <Button type="button" size="sm" onClick={() => void onSaveManual()} disabled={saving}>
                  Speichern
                </Button>
                {editing ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setEditing(null);
                      setDraft(emptyManualEvent());
                    }}
                  >
                    Abbrechen
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </Card>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {success ? <p className="text-sm text-emerald-700">Gespeichert.</p> : null}

      {canEdit ? (
        <Button type="button" onClick={() => void onSaveConfig()} disabled={saving}>
          {saving ? "Speichern…" : "Einstellungen speichern"}
        </Button>
      ) : null}
    </div>
  );
}
